/**
 * useApplyTokenTransforms
 *
 * React-side wiring around the pure {@link applyTokenTransforms} util.
 *
 * Reads the currently selected node from the node editor store and pulls the
 * `wrapActionInImplicitForeach` action from the workflow store, then hands
 * the resulting context to the util. After a NEW ForEach wrapper is created
 * we also flush cached `runAfter` state on the wrapped node — historically
 * this lived inside the util, but it's a store mutation so it belongs on
 * the React side now that the util is pure.
 *
 * Returns a stable function reference suitable for inclusion in
 * `useCallback` dependency arrays.
 */

import { useCallback } from 'react';
import type { Token } from '@/types/valueSegment';
import { useNodeEditorStore } from '@/stores/nodeEditorStore';
import { useWorkflowStore } from '@/stores/workflowStore';
import {
  applyTokenTransforms,
  type ApplyArrayTokenTransformsContext,
} from '@/utils/tokens';

export function useApplyTokenTransforms(): (token: Token) => Token {
  return useCallback((token: Token) => {
    const ctx: ApplyArrayTokenTransformsContext = {
      selectedNode: useNodeEditorStore.getState().node ?? null,
      // Lazy access to the workflow store — only consulted when the token
      // actually has implicitForeachExpression set. Avoids coupling tests
      // that exercise no-op paths to the workflow store shape.
      wrapActionInImplicitForeach: (actionName, expr) =>
        useWorkflowStore.getState().wrapActionInImplicitForeach(actionName, expr),
      onForeachCreated: () => {
        // Flush cached runAfter on the wrapped node so it's re-derived from
        // its new (ForEach-wrapped) location. Called only when a fresh
        // wrapper was just created.
        const post = useNodeEditorStore.getState();
        if (!post.node?.nodeData) return;
        let clearedSettings = post.settingsValues;
        if (clearedSettings?.runAfter) {
          const { runAfter: _, ...rest } = clearedSettings;
          clearedSettings = rest;
        }
        useNodeEditorStore.setState({
          node: {
            ...post.node,
            nodeData: { ...post.node.nodeData, runAfter: {} },
          },
          settingsValues: clearedSettings,
        });
      },
    };

    return applyTokenTransforms(token, ctx);
  }, []);
}
